/* ============================================================
   [CLAUDE 2026-08-12] Gallery photo <-> variant sync (all products)
   Supersedes assets/claude-enamel-sync.js
   ------------------------------------------------------------
   TWO DIRECTIONS

   1. PHOTO -> DROPDOWN. Move the gallery to a photo that belongs to
      a variant and the selectors follow it.
   2. DROPDOWN -> PHOTO. Pick a variant and the gallery shows it.
      The theme already does this for variants that HAVE a photo of
      their own (measured on Abigail: setting Color to 14K Rose Gold
      moved the main image to Rose_Gold_Name_Necklace_-_Abigail.jpg).
      This file adds the missing half: variants with NO photo now
      fall back to the photo of the same COLOUR.

   THE SEGAL COLOUR RULE (Yaniv, 2026-08-12)
   Photography is per colour, not per karat.
     * Platinum shows the WHITE GOLD photo - they look the same.
     * 18K shows the 14K photo of the same colour.
   Measured across 25 Name Necklaces, the same shape every time:
   only 14K Yellow / White / Rose are photographed; 18K Yellow,
   18K White, 18K Rose and Platinum have no image at all. The
   Initial Rings are already set up the correct way and were used
   as the reference.

   This rule is what makes the sync safe. Knowing a photo means
   "rose" rather than "14K rose" lets us change ONLY the colour and
   keep the customer's karat, so the price never moves underneath
   them. See swapColourOnly().

   WHY IT REPLACES THE ENAMEL VERSION
   claude-enamel-sync.js gated on the option NAME:
       if (String(oName).toLowerCase().indexOf('enamel') !== -1)
       if (optIndex === -1) return;
   Name necklaces, diamond name necklaces, initial rings and the
   Hebrew letter pendants all call that option "Color", so it
   returned immediately while those products had the same problem.
   Measured 2026-08-12 by clicking the gallery next arrow: Abigail
   showed the Rose Gold photo with the dropdown still on 14K Yellow
   Gold; same on Anastasia and Gold Initial A Ring.

   IT REFUSES TO GUESS. If the customer's exact configuration has no
   counterpart in the photo's colour - Platinum viewing a rose gold
   photo, when no rose platinum exists - nothing happens. An earlier
   build moved a customer who had chosen 18K Yellow Gold at $861 to
   14K Rose Gold at $570 without being asked. A stale dropdown is a
   smaller harm than silently changing what someone is buying.

   Products with no metal-colour option (the enamel rings, where the
   values are Pastel Black, Gray, Lemon Chiffon...) fall through to
   a general nearest-match path, so they keep working exactly as
   before - verified, including that the karat survives.

   Deliberately NO jQuery: this site loads two jQuery copies
   asynchronously and slick's events fire on whichever instance won
   the race, so delegation is unreliable. MutationObserver on
   .slick-current plus native change events work regardless.

   FAIL-SAFE: everything is in try/catch with early returns. If the
   product has no variant photos, or the photo belongs to no variant,
   or the selects cannot be found, nothing happens.

   TO REVERT: in snippets/layout-get-includes-end.liquid swap the
   script tag back to claude-enamel-sync.js, which is still present
   in assets/ and unmodified.
   ============================================================ */
(function () {
  'use strict';
  try {
    if (!/\/products\//.test(window.location.pathname)) return;

    /* Shopify serves one image under many transformed names. Reduce to
       the bare filename so a variant's featured_image can be compared
       with whatever the gallery actually rendered. */
    var normalize = function (src) {
      if (!src) return '';
      var f = String(src).split('?')[0].split('/').pop().toLowerCase();
      f = f.replace(/\.progressive(?=\.[a-z0-9]+$)/, '');
      f = f.replace(/@2x(?=\.[a-z0-9]+$)/, '');
      f = f.replace(/_(\d+x\d*|x\d+)(_crop_[a-z]+)?(?=\.[a-z0-9]+$)/, '');
      return f;
    };

    var getHandle = function () {
      var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      return m ? m[1] : null;
    };

    var tokens = function (s) {
      return String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    };

    /* THE COLOUR RULE. Platinum deliberately maps to white - it is not
       a white gold, but it photographs the same and Segal shoots one
       image for both. Anything unrecognised returns null and the
       colour logic simply does not engage for that product. */
    var colourOf = function (value) {
      var v = String(value || '').toLowerCase();
      if (v.indexOf('rose') !== -1 || v.indexOf('pink') !== -1) return 'rose';
      if (v.indexOf('white') !== -1) return 'white';
      if (v.indexOf('yellow') !== -1) return 'yellow';
      if (v.indexOf('platinum') !== -1) return 'white';
      return null;
    };

    /* Everything in the value that is NOT the colour - "18K", "Gold",
       "Platinum". Two values with the same rest and different colours
       are the same product in a different finish. */
    var rest = function (value) {
      return tokens(value)
        .filter(function (t) {
          return t !== 'rose' && t !== 'pink' && t !== 'white' && t !== 'yellow';
        })
        .sort()
        .join(' ');
    };

    var score = function (candidateOptions, currentValues) {
      var s = 0;
      for (var i = 0; i < candidateOptions.length; i++) {
        var cand = candidateOptions[i], now = currentValues[i];
        if (now == null) continue;
        if (cand === now) { s += 100; continue; }
        var a = tokens(cand), b = tokens(now), shared = 0;
        for (var j = 0; j < a.length; j++) if (b.indexOf(a[j]) !== -1) shared++;
        s += shared;
      }
      return s;
    };

    var init = function () {
      try {
        var handle = getHandle();
        if (!handle) return;
        var slider = document.querySelector('.product-gallery__main_slider');
        if (!slider) return;

        fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (product) {
            try {
              if (!product || !product.options || !product.variants) return;
              var optCount = product.options.length;

              var photoToVariants = {};
              product.variants.forEach(function (v) {
                if (!v.featured_image || !v.featured_image.src || !v.options) return;
                (photoToVariants[normalize(v.featured_image.src)] =
                  photoToVariants[normalize(v.featured_image.src)] || []).push(v);
              });
              if (Object.keys(photoToVariants).length < 2) return;

              /* Which option carries the metal colour? The one where most
                 values resolve to a colour. Null on the enamel rings. */
              var colourIdx = -1, bestHits = 0;
              for (var i = 0; i < optCount; i++) {
                var vals = (product.options[i] && product.options[i].values) || [];
                var hits = 0;
                for (var j = 0; j < vals.length; j++) if (colourOf(vals[j])) hits++;
                if (hits > bestHits && hits >= 2) { bestHits = hits; colourIdx = i; }
              }

              /* Every option value that any photographed variant offers. */
              var photographed = [];
              for (var p = 0; p < optCount; p++) photographed.push({});
              Object.keys(photoToVariants).forEach(function (k) {
                photoToVariants[k].forEach(function (v) {
                  for (var q = 0; q < optCount; q++) {
                    if (v.options[q] != null) photographed[q][v.options[q]] = true;
                  }
                });
              });

              var selectsForOption = function (i) {
                var values = (product.options[i] && product.options[i].values) || [];
                if (!values.length) return [];
                var list = [], all = document.querySelectorAll('select');
                for (var a = 0; a < all.length; a++) {
                  var s = all[a];
                  if (!s.options || !s.options.length) continue;
                  for (var b = 0; b < s.options.length; b++) {
                    if (values.indexOf(s.options[b].value) !== -1) { list.push(s); break; }
                  }
                }
                return list;
              };

              var driverFor = function (i) {
                var d = document.getElementById('variant-selector-option-' + i);
                if (d) return d;
                var list = selectsForOption(i);
                return list.length ? list[0] : null;
              };

              var currentValues = function () {
                var out = [];
                for (var i = 0; i < optCount; i++) {
                  var d = driverFor(i);
                  out.push(d ? d.value : null);
                }
                return out;
              };

              var variantFor = function (values) {
                for (var i = 0; i < product.variants.length; i++) {
                  var v = product.variants[i], same = true;
                  for (var j = 0; j < optCount; j++) {
                    if (v.options[j] !== values[j]) { same = false; break; }
                  }
                  if (same) return v;
                }
                return null;
              };

              var repaint = function (selectEl, value) {
                try {
                  var box = selectEl.closest ? selectEl.closest('.custom-select-container') : null;
                  if (!box) return;
                  var opener = box.querySelector('.custom-select-opener span');
                  var chosen = null;
                  for (var i = 0; i < selectEl.options.length; i++) {
                    if (selectEl.options[i].value === value) { chosen = selectEl.options[i]; break; }
                  }
                  if (opener && chosen) opener.textContent = chosen.text;
                  var opts = box.querySelectorAll('.custom-select-option');
                  for (var j = 0; j < opts.length; j++) {
                    opts[j].classList.toggle('is-selected', opts[j].getAttribute('data-value') === value);
                  }
                } catch (e) { /* cosmetic only */ }
              };

              var syncing = false;
              var releaseSoon = function () {
                window.setTimeout(function () { syncing = false; }, 600);
              };

              var applyVariant = function (target, now) {
                syncing = true;
                for (var i = 0; i < optCount; i++) {
                  var want = target.options[i];
                  if (want == null || want === now[i]) continue;
                  var driver = driverFor(i);
                  if (!driver) continue;
                  var exists = false;
                  for (var e = 0; e < driver.options.length; e++) {
                    if (driver.options[e].value === want) { exists = true; break; }
                  }
                  if (!exists) continue;
                  driver.value = want;
                  driver.dispatchEvent(new Event('change', { bubbles: true }));
                  var mirrors = selectsForOption(i);
                  for (var m = 0; m < mirrors.length; m++) {
                    if (mirrors[m] !== driver) mirrors[m].value = want;
                    repaint(mirrors[m], want);
                  }
                }
                releaseSoon();
              };

              /* Change ONLY the colour, keep everything else byte-identical.
                 This is what preserves the karat and therefore the price.
                 Returns null when the customer's exact configuration has no
                 counterpart in that colour - e.g. Platinum and a rose photo,
                 because no rose platinum exists. Null means do nothing. */
              var swapColourOnly = function (now, wantColour) {
                if (colourIdx === -1 || !wantColour) return null;
                var mine = now[colourIdx];
                if (mine == null) return null;
                var myRest = rest(mine);
                for (var i = 0; i < product.variants.length; i++) {
                  var v = product.variants[i];
                  if (colourOf(v.options[colourIdx]) !== wantColour) continue;
                  if (rest(v.options[colourIdx]) !== myRest) continue;
                  var ok = true;
                  for (var j = 0; j < optCount; j++) {
                    if (j === colourIdx) continue;
                    if (v.options[j] !== now[j]) { ok = false; break; }
                  }
                  if (ok) return v;
                }
                return null;
              };

              /* ---------------- photo -> dropdown ---------------- */
              var syncFromSlide = function () {
                try {
                  if (syncing) return;
                  var slide = slider.querySelector('.slick-current');
                  if (!slide) return;
                  var img = slide.querySelector('img');
                  if (!img) return;
                  var key = normalize(img.currentSrc || img.src ||
                    img.getAttribute('data-src') || img.getAttribute('data-lazy') || '');

                  var candidates = photoToVariants[key];
                  if (!candidates || !candidates.length) return;   /* lifestyle shot */

                  var now = currentValues();

                  for (var c = 0; c < candidates.length; c++) {
                    var same = true;
                    for (var i = 0; i < optCount; i++) {
                      if (candidates[c].options[i] !== now[i]) { same = false; break; }
                    }
                    if (same) return;   /* exactly what is selected */
                  }

                  var photoColour = colourIdx === -1 ? null
                    : colourOf(candidates[0].options[colourIdx]);

                  if (photoColour) {
                    /* Decide the colour option on its own, then take every OTHER
                       option from the photo. Keeping these separate matters: on
                       the enamel rings the photo encodes the metal colour AND the
                       enamel colour, and an earlier build that short-circuited on
                       "colour already matches" stopped the enamel sync working
                       altogether. Caught by the unit tests. */
                    var wantColourValue;
                    if (colourOf(now[colourIdx]) === photoColour) {
                      /* Same colour already - keep their exact value, which is
                         what preserves 18K, and Platinum on a white gold photo. */
                      wantColourValue = now[colourIdx];
                    } else {
                      var swapped = swapColourOnly(now, photoColour);
                      if (!swapped) return;   /* no counterpart - do not guess */
                      wantColourValue = swapped.options[colourIdx];
                    }

                    /* For the non-colour options, follow the candidate that
                       already agrees with the customer most. */
                    var pick = null, pickScore = -1;
                    for (var pc = 0; pc < candidates.length; pc++) {
                      var agree = 0;
                      for (var pj = 0; pj < optCount; pj++) {
                        if (pj === colourIdx) continue;
                        if (candidates[pc].options[pj] === now[pj]) agree++;
                      }
                      if (agree > pickScore) { pickScore = agree; pick = candidates[pc]; }
                    }

                    var target = now.slice();
                    target[colourIdx] = wantColourValue;
                    for (var tj = 0; tj < optCount; tj++) {
                      if (tj === colourIdx) continue;
                      if (pick && pick.options[tj] != null) target[tj] = pick.options[tj];
                    }

                    var changed = false;
                    for (var ck = 0; ck < optCount; ck++) {
                      if (target[ck] !== now[ck]) { changed = true; break; }
                    }
                    if (!changed) return;   /* visually in sync already */

                    var targetVariant = variantFor(target);
                    if (!targetVariant) return;   /* combination does not exist */
                    applyVariant(targetVariant, now);
                    return;
                  }

                  /* No metal-colour option (enamel rings). Nearest match among
                     the photo's own variants, and never act if the customer is
                     on something no photo represents. */
                  for (var u = 0; u < optCount; u++) {
                    if (now[u] != null && !photographed[u][now[u]]) return;
                  }
                  var currentVar = variantFor(now);
                  var best = null, bestScore = -1;
                  for (var d = 0; d < candidates.length; d++) {
                    var sc = score(candidates[d].options, now);
                    if (sc > bestScore) { bestScore = sc; best = candidates[d]; }
                    else if (sc === bestScore && best && currentVar &&
                             typeof candidates[d].price === 'number' &&
                             Math.abs(candidates[d].price - currentVar.price) <
                             Math.abs(best.price - currentVar.price)) {
                      best = candidates[d];
                    }
                  }
                  if (best) applyVariant(best, now);
                } catch (e) { /* never break the page */ }
              };

              /* ---------------- dropdown -> photo ----------------
                 Only for variants the theme cannot handle itself, i.e. those
                 with no featured_image: 18K and Platinum. Show the photo of
                 the same colour. */
              var slideIndexFor = function (key) {
                var slides = slider.querySelectorAll('.slick-slide:not(.slick-cloned)');
                for (var i = 0; i < slides.length; i++) {
                  var im = slides[i].querySelector('img');
                  if (!im) continue;
                  var k = normalize(im.currentSrc || im.src ||
                    im.getAttribute('data-src') || im.getAttribute('data-lazy') || '');
                  if (k && k === key) {
                    var idx = parseInt(slides[i].getAttribute('data-slick-index'), 10);
                    return isNaN(idx) ? i : idx;
                  }
                }
                return -1;
              };

              var showColourPhoto = function () {
                try {
                  if (syncing) return;
                  if (colourIdx === -1) return;
                  var now = currentValues();
                  var mine = variantFor(now);
                  /* The theme handles variants that have their own photo. */
                  if (!mine || (mine.featured_image && mine.featured_image.src)) return;

                  var wantColour = colourOf(now[colourIdx]);
                  if (!wantColour) return;

                  /* Prefer a twin that matches on the other options too. */
                  var twin = null, loose = null;
                  for (var i = 0; i < product.variants.length; i++) {
                    var v = product.variants[i];
                    if (!v.featured_image || !v.featured_image.src) continue;
                    if (colourOf(v.options[colourIdx]) !== wantColour) continue;
                    if (!loose) loose = v;
                    var ok = true;
                    for (var j = 0; j < optCount; j++) {
                      if (j === colourIdx) continue;
                      if (v.options[j] !== now[j]) { ok = false; break; }
                    }
                    if (ok) { twin = v; break; }
                  }
                  var use = twin || loose;
                  if (!use) return;

                  var key = normalize(use.featured_image.src);
                  var cur = slider.querySelector('.slick-current img');
                  if (cur) {
                    var curKey = normalize(cur.currentSrc || cur.src ||
                      cur.getAttribute('data-src') || cur.getAttribute('data-lazy') || '');
                    if (curKey === key) return;   /* already showing it */
                  }
                  var idx = slideIndexFor(key);
                  if (idx < 0) return;
                  var api = slider.slick && typeof slider.slick.slickGoTo === 'function'
                    ? slider.slick : null;
                  if (!api) return;
                  syncing = true;
                  try { api.slickGoTo(idx); } catch (e) { /* ignore */ }
                  releaseSoon();
                } catch (e) { /* never break the page */ }
              };

              var timer = null;
              var observer = new MutationObserver(function (mutations) {
                try {
                  var relevant = false;
                  for (var i = 0; i < mutations.length; i++) {
                    var m = mutations[i];
                    if (m.type === 'attributes' && m.attributeName === 'class' &&
                        m.target && m.target.classList &&
                        m.target.classList.contains('slick-current')) { relevant = true; break; }
                  }
                  if (!relevant) return;
                  if (timer) window.clearTimeout(timer);
                  timer = window.setTimeout(syncFromSlide, 200);
                } catch (e) { /* never break the page */ }
              });
              observer.observe(slider, { subtree: true, attributes: true, attributeFilter: ['class'] });

              /* Watch the selects for the other direction. Capture phase and
                 document level so it works whichever jQuery owns the widget. */
              var selTimer = null;
              document.addEventListener('change', function (ev) {
                try {
                  if (!ev.target || ev.target.tagName !== 'SELECT') return;
                  if (selTimer) window.clearTimeout(selTimer);
                  selTimer = window.setTimeout(showColourPhoto, 250);
                } catch (e) { /* never break the page */ }
              }, true);

              /* and once on load, for ?variant= deep links */
              window.setTimeout(showColourPhoto, 1200);
            } catch (e) { /* never break the page */ }
          })
          .catch(function () { /* network error: leave the page alone */ });
      } catch (e) { /* never break the page */ }
    };

    var tries = 0;
    var poll = window.setInterval(function () {
      tries += 1;
      if (document.querySelector('.product-gallery__main_slider')) {
        window.clearInterval(poll);
        init();
      } else if (tries > 120) {
        window.clearInterval(poll);
      }
    }, 250);
  } catch (e) { /* never break the page */ }
})();
